import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { resolveImage } from "../lib/api";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

// Bento spans for asymmetrical grid
const SPANS = [
  "md:col-span-7 md:row-span-2",
  "md:col-span-5",
  "md:col-span-5",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-6",
  "md:col-span-6",
  "md:col-span-5",
  "md:col-span-7",
];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/gallery/categories").then((r) => setCategories(r.data.categories)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/gallery", { params: filter !== "All" ? { category: filter } : {} })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <div data-testid="gallery-page" className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
      {/* Header */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
        <div className="md:col-span-8">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#71717A] mb-6">
            The vault — {items.length} pieces
          </p>
          <h1 className="font-display font-black text-6xl md:text-8xl leading-[0.85] tracking-tighter">
            The
            <br />
            <span className="relative inline-block">
              gallery
              <span className="absolute -bottom-3 left-0 h-3 w-full bg-[#00E5FF] -z-10" />
            </span>
          </h1>
        </div>
        <div className="md:col-span-4">
          <p className="font-mono text-sm leading-relaxed text-[#27272A]">
            Selected runs, capsule drops and one-offs printed by our team. Click any piece to read the spec sheet and leave a note for the studio.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="border-y border-black/10 py-6 mb-12 flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setFilter(c)}
            className={`px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] border transition-all ${
              filter === c
                ? "bg-black text-white border-black"
                : "bg-white text-black border-black/15 hover:border-black"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div data-testid="gallery-loading" className="font-mono text-sm text-[#71717A]">
          Loading prints…
        </div>
      ) : items.length === 0 ? (
        <div data-testid="gallery-empty" className="py-32 text-center">
          <p className="font-display font-black text-3xl">No prints in this category yet.</p>
        </div>
      ) : (
        <motion.div
          key={filter}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 auto-rows-[320px] md:auto-rows-[360px]"
        >
          {items.map((item, idx) => (
            <motion.div
              key={item.id}
              variants={fadeUp}
              className={`${SPANS[idx % SPANS.length]} group relative overflow-hidden border border-black/10 bg-white`}
              data-testid={`gallery-item-${item.id}`}
            >
              <Link to={`/gallery/${item.id}`} className="block w-full h-full">
                <div className="absolute inset-0 overflow-hidden bg-white">
                  <img
                    src={resolveImage(item.image_url)}
                    alt={item.title}
                    width="1200"
                    height="1200"
                    loading={idx < 6 ? "eager" : "lazy"}
                    decoding="async"
                    style={{
                      imageRendering: "auto",
                      filter: "contrast(1.04) saturate(1.06)",
                      objectPosition: "center",
                    }}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] mb-2 text-[#FFD600]">
                    {item.category}
                  </p>
                  <p className="font-display font-black text-2xl leading-tight">
                    {item.title}
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
